.panel {
  display: grid;
  gap: var(--space-5);
}

/*
 * The one elevated surface on this panel. It states a count and the rule that makes a
 * repeated deal legible, rather than a headline figure with a deadline attached — the
 * auction panel above is the surface with a deadline, and two masthead cards on one page
 * would compete for the same attention.
 */
.summary {
  display: grid;
  gap: var(--space-3);
}

.headline {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
}

.total {
  font-family: var(--font-figure);
  font-size: var(--text-2xl);
  font-weight: 600;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.totalLabel {
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

/* Canon § 7's independence rule, treated as furniture rather than as a warning. */
.note {
  padding-block-start: var(--space-3);
  border-block-start: 1px solid var(--border);
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.scope {
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.days {
  display: grid;
  gap: var(--space-6);
}

.day {
  display: grid;
  gap: var(--space-3);
}

/*
 * The display day is the organising fact of this panel, so it is set in the figure face —
 * the face Dealbid uses everywhere for a date, a rank or an amount.
 *
 * It is a row label inside a panel, not a section heading, so `APP-014` left the treatment
 * alone: what that task withdrew was the brass caption *above a title*, and this is neither
 * brass nor above one. Only the wording, which called it "the board's own eyebrow
 * treatment", had to change.
 */
.dayHeading {
  font-family: var(--font-figure);
  font-size: var(--text-sm);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text);
}

.rows {
  display: grid;
  gap: var(--space-3);
  /* Not read as prose, so it clears its own marker: `base.css` zeroes list margin and
   * padding for every repo but deliberately leaves `list-style` alone (`APP-018`).
   * `role="list"` on the element keeps the semantics Safari drops with the marker. */
  list-style: none;
}

.row {
  display: grid;
  gap: var(--space-3);
  border-inline-start: 3px solid var(--border-strong);
}

/*
 * A settled row is edged in solid brass — the board's own rank furniture. A standing one is
 * edged in a *dashed* action green: still open, not yet a result. The difference is in the
 * stroke as well as the hue, so it survives a monochrome rendering, and neither edge is the
 * only thing saying so — every row states its outcome in a pill carrying a shaped mark and
 * the word itself (contracts/DESIGN-TOKENS.md § Accessibility).
 */
.settled {
  border-inline-start-color: var(--brass-rule);
}

.standing {
  border-inline-start-color: var(--accent);
  border-inline-start-style: dashed;
}

/*
 * Mobile-first: the amount, the deal and the verdict stack. From 40rem the row becomes a
 * ledger line — amount, what it was for, what became of it — with the pill at the end.
 */
.head {
  display: grid;
  gap: var(--space-2);
}

@media (min-width: 40rem) {
  .head {
    grid-template-columns: 5rem minmax(0, 1fr) auto;
    align-items: baseline;
    gap: var(--space-4);
  }
}

/*
 * The amount is this panel's mark, where the placement history's mark is a rank. A bid is
 * money committed, and that is the figure a founder scans down the column for.
 */
.amount {
  font-family: var(--font-figure);
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.names {
  display: grid;
  gap: var(--space-1);
  min-inline-size: 0;
}

.product {
  font-size: var(--text-lg);
  min-inline-size: 0;
}

.result {
  color: var(--text-muted);
  font-size: var(--text-sm);
}

@media (min-width: 40rem) {
  .pill {
    justify-self: end;
  }
}

/* What happened to the bid and to the credit it reserved. */
.why {
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}
